import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import AshtavargaChakra from '../charts/AshtavargaChakra';
import ChartContainer from '../vedic/results/ChartContainer';
import ResponsiveTable from '../vedic/results/ResponsiveTable';
import DashaTimeline from '../vedic/results/DashaTimeline';
import LoadingState from '../vedic/feedback/LoadingState';
import BirthDataForm from '../vedic/forms/BirthDataForm';
import { to24Hour } from '../vedic/forms/TimeInput';
import ResultTabs from '../vedic/results/ResultTabs';
import { freeKundliService, savedHoroscopeService } from '../../services/api';
import { getLocale, availableLanguages } from '../../locales';
import { useAnalytics } from '../../analytics/hooks';
import { useAuth } from '../../contexts/AuthContext';
import PdfExportPanel from '../vedic/pdf/PdfExportPanel';

/* ─── Styled Components ─── */

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 10px;
  color: ${p => p.theme.colors.text.primary};
`;

const PageSubtitle = styled.p`
  text-align: center;
  color: ${p => p.theme.colors.text.secondary};
  margin-bottom: 30px;
  font-size: 0.95rem;
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.radius.md}px;
  box-shadow: ${p => p.theme.shadow.card};
  padding: 30px;
`;

const CardTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 20px;
  color: ${p => p.theme.colors.text.primary};
  text-align: center;
`;


const MatchingLink = styled.button`
  background: none;
  border: 2px solid ${p => p.theme.colors.bands.exceptional};
  color: ${p => p.theme.colors.bands.exceptional};
  border-radius: ${p => p.theme.radius.sm}px;
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.3s;
  &:hover { background: ${p => p.theme.colors.bands.exceptional}; color: ${p => p.theme.colors.surface}; }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-top: 30px;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

/* PlanetTable replaced by shared ResponsiveTable component */

const NoChartBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${p => p.theme.colors.text.muted};
  text-align: center;
  h3 { margin-bottom: 8px; }
`;


/* ─── Saved Horoscopes ─── */

const SavedSection = styled.div`
  margin-top: 16px;
  border-top: 1px solid ${p => p.theme.colors.border};
  padding-top: 16px;
`;

const SavedTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 10px;
  color: ${p => p.theme.colors.text.primary};
`;

const SavedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
`;

const SavedEmptyState = styled.p`
  font-size: 0.85rem;
  color: ${p => p.theme.colors.text.muted};
  font-style: italic;
  margin: 6px 0 10px;
  padding: 8px 12px;
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.radius.sm}px;
  border: 1px dashed ${p => p.theme.colors.border};
`;

const SavedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.radius.sm}px;
  border: 1px solid ${p => p.theme.colors.border};
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover { border-color: ${p => p.theme.colors.bands.exceptional}; }
`;

const SavedItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name { font-weight: 600; font-size: 0.9rem; color: ${p => p.theme.colors.text.primary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .meta { font-size: 0.78rem; color: ${p => p.theme.colors.text.muted}; }
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.text.muted};
  cursor: pointer;
  font-size: 1.1rem;
  padding: 4px 6px;
  border-radius: 4px;
  &:hover { color: #c62828; background: #ffebee; }
`;

const SaveBtn = styled.button`
  background: none;
  border: 2px solid ${p => p.theme.colors.bands.strong};
  color: ${p => p.theme.colors.bands.strong};
  border-radius: ${p => p.theme.radius.sm}px;
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.3s;
  &:hover { background: ${p => p.theme.colors.bands.strong}; color: ${p => p.theme.colors.surface}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ─── Language Toggle ─── */

const LangToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-bottom: 16px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${props => props.active ? props.theme.colors.bands.exceptional : props.theme.colors.border};
  background: ${props => props.active ? props.theme.colors.bands.exceptional : props.theme.colors.surface};
  color: ${props => props.active ? props.theme.colors.surface : props.theme.colors.text.secondary};
  border-radius: ${p => p.theme.radius.sm}px;
  font-size: 0.85rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${p => p.theme.colors.bands.exceptional}; }
`;

/* ─── Tabbed Results ─── */

const TabContainer = styled.div`
  margin-top: 30px;
`;

const TabContent = styled.div`
  padding: 20px 0;
`;

/* ─── Dasha Components ─── */

const DashaSection = styled.div`
  margin-bottom: 20px;
`;

const DashaBalanceCard = styled.div`
  background: linear-gradient(135deg, ${p => p.theme.colors.bands.exceptional} 0%, #9c27b0 100%);
  color: ${p => p.theme.colors.surface};
  padding: 20px;
  border-radius: ${p => p.theme.radius.md}px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  @media (max-width: 576px) { grid-template-columns: 1fr; }
`;

const DashaBalanceItem = styled.div`
  text-align: center;
  .label { font-size: 0.8rem; opacity: 0.8; margin-bottom: 4px; }
  .value { font-size: 1.3rem; font-weight: 600; }
`;

const CurrentDashaCard = styled.div`
  background: #f3e5f5;
  padding: 16px 20px;
  border-radius: ${p => p.theme.radius.md}px;
  border-left: 4px solid ${p => p.theme.colors.bands.exceptional};
  margin-bottom: 20px;
  .title { font-weight: 600; color: #4a148c; margin-bottom: 8px; }
  .periods { font-size: 0.95rem; color: ${p => p.theme.colors.text.primary}; }
  span { font-weight: 600; }
`;

const MahaDashaList = styled.div`
  display: grid;
  gap: 8px;
`;

const MahaDashaRow = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 150px 150px;
  align-items: center;
  padding: 10px 16px;
  background: ${props => props.isCurrent ? '#f3e5f5' : props.theme.colors.background};
  border-radius: 6px;
  border-left: 3px solid ${props => props.isCurrent ? props.theme.colors.bands.exceptional : props.theme.colors.border};
  font-size: 0.9rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 4px;
  }
  .lord { font-weight: 600; }
  .years { color: ${p => p.theme.colors.text.secondary}; }
  .dates { color: ${p => p.theme.colors.text.muted}; font-size: 0.85rem; }
`;

/* ─── Yoga/Dosha Components ─── */

const YogaCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.md}px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid ${props => {
    switch (props.category) {
      case 'power': return props.theme.colors.bands.challenging;
      case 'wealth': return props.theme.colors.bands.weak;
      case 'intellect': return props.theme.colors.bands.moderate;
      case 'protection': return props.theme.colors.bands.strong;
      case 'challenge': return props.theme.colors.text.muted;
      default: return props.theme.colors.bands.exceptional;
    }
  }};
`;

const YogaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  .name { font-weight: 600; font-size: 1.05rem; color: ${p => p.theme.colors.text.primary}; }
  .sanskrit { font-size: 0.85rem; color: ${p => p.theme.colors.text.muted}; margin-left: 8px; }
`;

const YogaBadge = styled.span`
  padding: 3px 10px;
  border-radius: ${p => p.theme.radius.lg}px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.strength) {
      case 'very strong': return '#c8e6c9';
      case 'strong': return '#dcedc8';
      case 'moderate': return '#fff9c4';
      case 'weak': return '#ffcdd2';
      default: return props.theme.colors.border;
    }
  }};
  color: ${props => {
    switch (props.strength) {
      case 'very strong': return '#1b5e20';
      case 'strong': return '#33691e';
      case 'moderate': return '#f57f17';
      case 'weak': return '#b71c1c';
      default: return props.theme.colors.text.primary;
    }
  }};
`;

const YogaDesc = styled.p`
  font-size: 0.9rem;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0 0 8px 0;
`;

const PlanetTags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const PlanetTag = styled.span`
  padding: 2px 8px;
  background: #e8eaf6;
  border-radius: ${p => p.theme.radius.sm}px;
  font-size: 0.8rem;
  color: #283593;
`;

/* ─── Dosha Components ─── */

const DoshaCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.radius.md}px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid ${props => {
    if (!props.present) return props.theme.colors.bands.strong;
    switch (props.severity) {
      case 'high': return props.theme.colors.bands.challenging;
      case 'medium': return props.theme.colors.bands.weak;
      case 'low': return '#ffc107';
      default: return props.theme.colors.text.muted;
    }
  }};
`;

const DoshaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  .name { font-weight: 600; font-size: 1.05rem; }
`;

const DoshaStatus = styled.span`
  padding: 3px 10px;
  border-radius: ${p => p.theme.radius.lg}px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.present ? '#ffcdd2' : '#c8e6c9'};
  color: ${props => props.present ? '#b71c1c' : '#1b5e20'};
`;

const RemedyList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  li { font-size: 0.85rem; color: ${p => p.theme.colors.text.secondary}; margin-bottom: 4px; }
`;

/* ─── Panchanga Components ─── */

const PanchangaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const PanchangaItem = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.radius.md}px;
  padding: 16px;
  text-align: center;
  border: 1px solid ${p => p.theme.colors.border};
  .label { font-size: 0.8rem; color: ${p => p.theme.colors.text.muted}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
  .value { font-size: 1.2rem; font-weight: 600; color: ${p => p.theme.colors.text.primary}; }
  .sub { font-size: 0.85rem; color: ${p => p.theme.colors.text.secondary}; margin-top: 4px; }
`;

/* ─── Main Component ─── */

const FreeKundli = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();
  const { isAuthenticated } = useAuth();
  const [lang, setLang] = useState('en');
  const t = getLocale(lang).freeKundli;
  const loc = getLocale(lang);
  const defaultFormValues = {
    name: '',
    gender: 'male',
    dob: '',
    hour: 1,
    minute: 0,
    period: 'AM',
    timeFormat: '12h',
    place: '',
    latitude: null,
    longitude: null,
    timezone: 'Asia/Kolkata',
    ayanamsa: 'lahiri',
  };
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [initialValues, setInitialValues] = useState(null);
  const [autoSubmitFlag, setAutoSubmitFlag] = useState(false);
  const [ayanamsaOpts, setAyanamsaOpts] = useState([]);

  // Fetch ayanamsa options from API
  useEffect(() => {
    freeKundliService.listAyanamsas()
      .then(res => {
        const list = res.data?.ayanamsas || [];
        setAyanamsaOpts(list);
      })
      .catch(() => {
        setAyanamsaOpts([
          { mode: 'lahiri', display_name: 'Lahiri' },
          { mode: 'kp', display_name: 'Krishnamurti (KP)' },
          { mode: 'raman', display_name: 'B.V. Raman' },
        ]);
      });
  }, []);

  // ─── Saved Horoscope Profiles ───
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [saveLabel, setSaveLabel] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Fetch saved profiles on mount (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    savedHoroscopeService.list()
      .then(res => setSavedProfiles(res.data?.data || []))
      .catch(() => { /* silently ignore for unauthenticated */ });
  }, [isAuthenticated]);

  const handleSaveProfile = async () => {
    if (!saveLabel.trim() || !formValues.dob || !formValues.latitude) return;
    setSavingProfile(true);
    try {
      const hour24 = formValues.timeFormat === '24h'
        ? formValues.hour
        : (formValues.period === 'AM'
            ? (parseInt(formValues.hour) === 12 ? 0 : parseInt(formValues.hour))
            : (parseInt(formValues.hour) === 12 ? 12 : parseInt(formValues.hour) + 12));
      const tob = `${String(hour24).padStart(2, '0')}:${String(formValues.minute).padStart(2, '0')}`;
      const payload = {
        label: saveLabel.trim(),
        dob: formValues.dob,
        tob,
        place: formValues.place,
        latitude: formValues.latitude,
        longitude: formValues.longitude,
      };
      const res = await savedHoroscopeService.create(payload);
      setSavedProfiles(prev => [res.data?.data, ...prev].filter(Boolean));
      setSaveLabel('');
      setShowSaveInput(false);
      track('horoscope_profile_saved', { label: payload.label });
    } catch {
      /* ignore — user will see the button stays */
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfile = async (id) => {
    try {
      await savedHoroscopeService.remove(id);
      setSavedProfiles(prev => prev.filter(p => p.id !== id));
    } catch { /* ignore */ }
  };

  const handleLoadProfile = (profile) => {
    const tobParts = (profile.tob || '').split(':');
    const hour24 = parseInt(tobParts[0]) || 0;
    const minute = parseInt(tobParts[1]) || 0;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;

    const newVals = {
      ...defaultFormValues,
      name: profile.label || '',
      dob: profile.dob || '',
      hour: hour12,
      minute,
      period,
      place: profile.place || '',
      latitude: profile.latitude || null,
      longitude: profile.longitude || null,
    };
    setFormValues(newVals);
    setInitialValues(newVals);
    setAutoSubmitFlag(false);
    setChartResult(null);
    setError(null);
    track('horoscope_profile_loaded', { label: profile.label });
  };

  const [chartResult, setChartResult] = useState(() => {
    try {
      const cached = sessionStorage.getItem('kundli_result_cache');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('chart');

  // One-time migration from old sessionStorage key
  useEffect(() => {
    const old = sessionStorage.getItem('kundli_form_cache');
    if (old) {
      sessionStorage.setItem('vedic_freeKundli_form', old);
      sessionStorage.removeItem('kundli_form_cache');
    }
  }, []);

  // Persist chart result to sessionStorage
  useEffect(() => {
    try {
      if (chartResult) {
        sessionStorage.setItem('kundli_result_cache', JSON.stringify(chartResult));
      }
    } catch { /* storage full or unavailable */ }
  }, [chartResult]);

  // Auto-populate from call context (astrologer clicking User Details during call)
  const autoPopulateRef = useRef(false);
  useEffect(() => {
    const ap = location.state?.autoPopulate;
    if (!ap || autoPopulateRef.current) return;
    autoPopulateRef.current = true;

    // Parse time_of_birth "HH:MM" or "HH:MM:SS" → 12-hour + period
    const timeParts = (ap.time_of_birth || '').split(':');
    const hour24 = parseInt(timeParts[0]) || 0;
    const minute = parseInt(timeParts[1]) || 0;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;

    setInitialValues({
      name: ap.name || '',
      gender: ap.gender || 'male',
      dob: ap.date_of_birth || '',
      hour: hour12,
      minute,
      period,
      place: ap.place_of_birth || '',
      latitude: ap.latitude || null,
      longitude: ap.longitude || null,
      timezone: ap.timezone || 'Asia/Kolkata',
      ayanamsa: 'lahiri',
    });
    setAutoSubmitFlag(true);
  }, [location.state]);

  const handleClear = () => {
    setFormValues(defaultFormValues);
    setChartResult(null);
    setActiveTab('chart');
    setError(null);
    sessionStorage.removeItem('vedic_freeKundli_form');
    sessionStorage.removeItem('kundli_result_cache');
  };

  const handleSubmit = async (vals) => {
    const fd = vals || formValues;
    setError(null);

    if (!fd.latitude || !fd.longitude) {
      setError(t.errSelectPlace);
      return;
    }

    const dobParts = (fd.dob || '').split('-');
    if (dobParts.length !== 3) {
      setError(t.errInvalidDob);
      return;
    }

    setLoading(true);
    track('kundli_started', { ayanamsa: fd.ayanamsa, chart_type: 'free' });
    try {
      const payload = {
        name: fd.name,
        gender: fd.gender || 'male',
        year: parseInt(dobParts[0]),
        month: parseInt(dobParts[1]),
        day: parseInt(dobParts[2]),
        hour: typeof fd.hour === 'number' && fd.timeFormat === '24h'
          ? fd.hour
          : to24Hour(parseInt(fd.hour) || 12, fd.period, fd.timeFormat || '12h'),
        minute: parseInt(fd.minute) || 0,
        place: fd.place,
        latitude: fd.latitude,
        longitude: fd.longitude,
        timezone: fd.timezone || 'Asia/Kolkata',
        ayanamsa: fd.ayanamsa,
        include_all: true,
      };

      const res = await freeKundliService.generateChart(payload);
      track('kundli_completed', { ayanamsa: fd.ayanamsa, chart_type: 'free' });
      setChartResult(res.data);
      setActiveTab('chart');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate kundli. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const chart = chartResult?.chart;
  const dasha = chartResult?.dasha;
  const yogas = chartResult?.yogas;
  const doshas = chartResult?.doshas;
  const panchanga = chartResult?.panchanga;
  const vargas = chartResult?.vargas;
  const shadbala = chartResult?.shadbala;
  const ashtakavarga = chartResult?.ashtakavarga;
  const houseAnalysis = chartResult?.house_analysis;
  const bhavaBala = chartResult?.bhava_bala;
  const vimshopakaBala = chartResult?.vimshopaka_bala;

  // Build available tabs
  const tabs = [{ key: 'chart', label: t.tabChart }];
  if (dasha) tabs.push({ key: 'dasha', label: t.tabDasha });
  if (yogas) tabs.push({ key: 'yogas', label: t.tabYogas, badge: yogas?.length });
  if (doshas) tabs.push({ key: 'doshas', label: t.tabDoshas, badge: doshas?.length });
  if (panchanga) tabs.push({ key: 'panchanga', label: t.tabPanchanga });
  if (shadbala) tabs.push({ key: 'shadbala', label: t.tabShadbala || 'Shadbala' });
  if (ashtakavarga) tabs.push({ key: 'ashtakavarga', label: t.tabAshtakavarga || 'Ashtakavarga' });
  if (vargas) tabs.push({ key: 'vargas', label: t.tabVargas || 'Divisional Charts' });
  if (houseAnalysis) tabs.push({ key: 'houses', label: t.tabHouses || 'Houses' });
  if (bhavaBala) tabs.push({ key: 'bhavabala', label: t.tabBhavaBala || 'Bhava Bala' });
  if (vimshopakaBala) tabs.push({ key: 'vimshopaka', label: t.tabVimshopaka || 'Vimshopaka' });

  return (
    <PageContainer>
      <LangToggle>
        {availableLanguages.map(l => (
          <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
            {l.label}
          </LangBtn>
        ))}
      </LangToggle>

      <PageTitle>{t.title}</PageTitle>
      <PageSubtitle>{t.subtitle}</PageSubtitle>

      <TopGrid>
        {/* Input Form */}
        <Card>
          <CardTitle>{t.enterBirthDetails}</CardTitle>
          <BirthDataForm
            fields={['name', 'gender', 'dob', 'time', 'place', 'ayanamsa']}
            values={formValues}
            onChange={setFormValues}
            onSubmit={handleSubmit}
            submitLabel={loading ? t.generating : t.generateKundli}
            loading={loading}
            error={error}
            initialValues={initialValues}
            autoSubmit={autoSubmitFlag}
            ayanamsaOptions={ayanamsaOpts}
            pageName="freeKundli"
            lang={lang}
          />

          {/* ─── Saved Horoscope Profiles (auth only) ─── */}
          {isAuthenticated && (
            <SavedSection>
              <SavedTitle>
                {lang === 'ta' ? 'சேமித்த ஜாதகங்கள்' : 'Saved Horoscopes'}
              </SavedTitle>

              {/* List saved profiles */}
              {savedProfiles.length === 0 ? (
                <SavedEmptyState>
                  {lang === 'ta'
                    ? 'இன்னும் சேமிக்கவில்லை — கீழே உள்ள "சேமி" பொத்தானை அழுத்துங்கள்'
                    : 'No saved profiles yet — fill in birth details and click Save'}
                </SavedEmptyState>
              ) : (
                <SavedList>
                  {savedProfiles.map(p => (
                    <SavedItem key={p.id} onClick={() => handleLoadProfile(p)}>
                      <SavedItemInfo>
                        <div className="name">{p.label}</div>
                        <div className="meta">{p.dob} &middot; {p.place}</div>
                      </SavedItemInfo>
                      <DeleteBtn
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeleteProfile(p.id); }}
                        title={lang === 'ta' ? 'நீக்கு' : 'Delete'}
                      >
                        &#x1F5D1;
                      </DeleteBtn>
                    </SavedItem>
                  ))}
                </SavedList>
              )}

              {/* Save current form */}
              {!showSaveInput ? (
                <SaveBtn
                  type="button"
                  onClick={() => setShowSaveInput(true)}
                  disabled={!formValues.dob || !formValues.latitude}
                >
                  {lang === 'ta' ? 'சேமி' : 'Save This Profile'}
                </SaveBtn>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    type="text"
                    value={saveLabel}
                    onChange={(e) => setSaveLabel(e.target.value)}
                    placeholder={lang === 'ta' ? 'பெயர் கொடுங்கள்...' : 'Profile name...'}
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: 6,
                      border: '1px solid #ccc', fontSize: '0.9rem',
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveProfile(); }}
                    autoFocus
                  />
                  <SaveBtn
                    type="button"
                    style={{ width: 'auto', marginTop: 0, padding: '8px 16px' }}
                    onClick={handleSaveProfile}
                    disabled={savingProfile || !saveLabel.trim()}
                  >
                    {savingProfile ? '...' : (lang === 'ta' ? 'சேமி' : 'Save')}
                  </SaveBtn>
                  <DeleteBtn type="button" onClick={() => { setShowSaveInput(false); setSaveLabel(''); }}>
                    &#x2715;
                  </DeleteBtn>
                </div>
              )}
            </SavedSection>
          )}

          <MatchingLink type="button" onClick={handleClear}>
            {lang === 'ta' ? 'அழி' : 'Clear'}
          </MatchingLink>

          <MatchingLink type="button" onClick={() => navigate('/kundli-matching')}>
            {t.matchingLink}
          </MatchingLink>

          <MatchingLink type="button" onClick={() => navigate('/today-panchangam')} style={{ marginTop: 8 }}>
            {t.panchangamLink || (lang === 'ta' ? 'இன்றைய பஞ்சாங்கம்' : "Today's Panchangam")}
          </MatchingLink>
        </Card>

        {/* Right side: Rasi chart or placeholder */}
        <Card>
          {loading && !chart ? (
            <LoadingState loading={loading} variant="chart" />
          ) : chart ? (
            <ChartContainer chartData={chart} title={t.rasiChakra} loc={loc} />
          ) : (
            <NoChartBox>
              <h3>{t.noChartTitle}</h3>
              <p>{t.noChartDesc}</p>
            </NoChartBox>
          )}
        </Card>
      </TopGrid>

      {/* PDF Export Panel — visible when chart is generated */}
      {chartResult && <PdfExportPanel chartResult={chartResult} lang={lang} />}

      {/* Tabbed Results */}
      {chart && (
        <TabContainer>
          <ResultTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            accentColor="#B8860B"
          />

          <TabContent>
            {activeTab === 'chart' && <ChartTab chart={chart} chartResult={chartResult} loc={loc} />}
            {activeTab === 'dasha' && dasha && <DashaTab dasha={dasha} loc={loc} />}
            {activeTab === 'yogas' && yogas && <YogasTab yogas={yogas} loc={loc} />}
            {activeTab === 'doshas' && doshas && <DoshasTab doshas={doshas} loc={loc} />}
            {activeTab === 'panchanga' && panchanga && <PanchangaTab panchanga={panchanga} loc={loc} />}
            {activeTab === 'shadbala' && shadbala && <ShadbalaTab shadbala={shadbala} loc={loc} />}
            {activeTab === 'ashtakavarga' && ashtakavarga && <AshtakavargaTab ashtakavarga={ashtakavarga} ascendantSign={chart?.ascendant?.sign_index} chart={chart} loc={loc} />}
            {activeTab === 'vargas' && vargas && <VargasTab vargas={vargas} loc={loc} />}
            {activeTab === 'houses' && houseAnalysis && <HouseAnalysisTab houseAnalysis={houseAnalysis} loc={loc} />}
            {activeTab === 'bhavabala' && bhavaBala && <BhavaBalaTab bhavaBala={bhavaBala} loc={loc} />}
            {activeTab === 'vimshopaka' && vimshopakaBala && <VimshopkaTab vimshopaka={vimshopakaBala} loc={loc} />}
          </TabContent>
        </TabContainer>
      )}
    </PageContainer>
  );
};

/* ─── Tab Content Components ─── */

const ChartTab = ({ chart, chartResult, loc }) => {
  const t = loc.freeKundli;
  const tp = loc.planets;
  const ts = loc.signs;
  const tn = loc.nakshatras;
  return (
    <>
      <ChartsGrid>
        <Card>
          <ChartContainer chartData={chart} title={t.rasiChakra} loc={loc} />
        </Card>
        <Card>
          <ChartContainer chartData={chart} title={t.navamsaChakra} isNavamsa loc={loc} />
        </Card>
      </ChartsGrid>

      <Card style={{ marginTop: 30 }}>
        <CardTitle>{t.planetDetails}</CardTitle>
        <ResponsiveTable
          columns={[
            { key: 'planet', label: t.colPlanet },
            { key: 'sign', label: t.colSign },
            { key: 'degree', label: t.colDegree },
            { key: 'nakshatra', label: t.colNakshatra },
            { key: 'pada', label: t.colPada },
            { key: 'navamsa', label: t.colNavamsa },
            { key: 'retrograde', label: t.colRetro },
          ]}
          rows={[
            {
              planet: tp.Ascendant || 'Ascendant',
              sign: ts[chart.ascendant.sign_name] || chart.ascendant.sign_name,
              degree: `${chart.ascendant.degree}\u00B0${String(chart.ascendant.minutes).padStart(2,'0')}'${String(chart.ascendant.seconds).padStart(2,'0')}"`,
              nakshatra: tn[chart.ascendant.nakshatra] || chart.ascendant.nakshatra,
              pada: chart.ascendant.pada,
              navamsa: ts[chart.ascendant.navamsa_name] || chart.ascendant.navamsa_name,
              retrograde: '-',
            },
            ...chart.planets.map(p => ({
              planet: tp[p.planet] || p.planet,
              sign: ts[p.sign_name] || p.sign_name,
              degree: `${p.degree}\u00B0${String(p.minutes).padStart(2,'0')}'${String(p.seconds).padStart(2,'0')}"`,
              nakshatra: tn[p.nakshatra] || p.nakshatra,
              pada: p.pada,
              navamsa: ts[p.navamsa_name] || p.navamsa_name,
              is_retrograde: p.is_retrograde,
              retrograde: p.is_retrograde ? 'R' : '-',
            })),
          ]}
        />
      </Card>

      <Card style={{ marginTop: 20, marginBottom: 40 }}>
        <CardTitle>{t.birthDetails}</CardTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.95rem' }}>
          <div><strong>{t.labelName}:</strong> {chartResult.name}</div>
          <div><strong>{t.labelGender}:</strong> {chartResult.gender}</div>
          <div><strong>{t.labelDate}:</strong> {chartResult.birth_details.date}</div>
          <div><strong>{t.labelTime}:</strong> {chartResult.birth_details.time}</div>
          <div><strong>{t.labelPlace}:</strong> {chartResult.birth_details.place}</div>
          <div><strong>{t.labelAyanamsa} ({chart.ayanamsa_mode}):</strong> {chart.ayanamsa}&deg;</div>
        </div>
      </Card>
    </>
  );
};

/* ─── Dasa Drill-down Popup Components ─── */

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const Popup = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.radius.lg}px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.2);
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 24px;
`;

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  h3 { margin: 0; font-size: 1.15rem; color: #4a148c; }
`;

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${p => p.theme.colors.text.muted};
  &:hover { color: ${p => p.theme.colors.text.primary}; }
`;

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const Breadcrumb = styled.div`
  font-size: 0.8rem;
  color: ${p => p.theme.colors.text.muted};
  margin-bottom: 12px;
  span { color: ${p => p.theme.colors.bands.exceptional}; font-weight: 600; }
`;

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const SubDashaRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr 120px 120px;
  align-items: center;
  padding: 10px 14px;
  background: ${props => props.isCurrent ? '#f3e5f5' : props.theme.colors.background};
  border-radius: 6px;
  border-left: 3px solid ${props => props.isCurrent ? props.theme.colors.bands.exceptional : props.theme.colors.border};
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${props => props.isCurrent ? '#e1bee7' : props.theme.colors.border}; }
  @media (max-width: 576px) {
    grid-template-columns: 1fr 1fr;
    gap: 4px;
  }
  .lord { font-weight: 600; }
  .dates { color: ${p => p.theme.colors.text.muted}; font-size: 0.8rem; }
`;

// eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
const SubDashaList = styled.div`
  display: grid;
  gap: 6px;
`;

const DashaClickHint = styled.div`
  font-size: 0.75rem;
  color: ${p => p.theme.colors.text.muted};
  text-align: center;
  margin-top: 10px;
  font-style: italic;
`;

const DashaTab = ({ dasha, loc }) => {
  const t = loc.freeKundli;
  const tp = loc.planets;
  const tn = loc.nakshatras;
  // TODO(#123): popup state + drill-down functions — remove with popup dasha code
  // eslint-disable-next-line no-unused-vars
  const [popup, setPopup] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const openBukthi = (md) => {
    setPopup({ level: 'bukthi', items: md.antardashas || [], breadcrumb: [{ lord: md.lord }] });
  };
  // eslint-disable-next-line no-unused-vars
  const openAnthara = (ad, parentPath) => {
    setPopup({ level: 'anthara', items: ad.pratyantdashas || [], breadcrumb: [...parentPath, { lord: ad.lord }] });
  };
  // eslint-disable-next-line no-unused-vars
  const openSukshuma = (pt, parentPath) => {
    setPopup({ level: 'sukshuma', items: pt.sukshumas || [], breadcrumb: [...parentPath, { lord: pt.lord }] });
  };
  // eslint-disable-next-line no-unused-vars
  const openPrana = (sk, parentPath) => {
    setPopup({ level: 'prana', items: sk.pranas || [], breadcrumb: [...parentPath, { lord: sk.lord }] });
  };

  const formatDuration = (dp) => {
    if (!dp) return '0';
    const p = [];
    if (dp.years > 0) p.push(`${dp.years} ${t.yrsUnit || 'Y'}`);
    if (dp.months > 0) p.push(`${dp.months} ${t.monthsUnit || 'M'}`);
    if (dp.days > 0) p.push(`${dp.days} ${t.daysUnit || 'D'}`);
    return p.join(' ') || '0';
  };

  // eslint-disable-next-line no-unused-vars -- TODO(#123): Remove with popup dasha code
  const levelLabels = {
    bukthi: t.bukthi || 'Bukthi',
    anthara: t.anthara || 'Anthara',
    sukshuma: t.sukshuma || 'Sukshuma',
    prana: t.prana || 'Prana',
  };

  return (
    <DashaSection>
      <Card>
        <CardTitle>{t.vimshottariDasha}</CardTitle>

        {/* Current Running Dasha */}
        <CurrentDashaCard>
          <div className="title">{t.currentlyRunning}</div>
          <div className="periods">
            <span>{tp[dasha.current_dasha.mahadasha] || dasha.current_dasha.mahadasha}</span> {t.mahadasha} &rarr;{' '}
            <span>{tp[dasha.current_dasha.antardasha] || dasha.current_dasha.antardasha}</span> {t.antardasha} &rarr;{' '}
            <span>{tp[dasha.current_dasha.pratyantdasha] || dasha.current_dasha.pratyantdasha}</span> {t.pratyantdasha}
            {dasha.current_dasha.sukshuma && (
              <> &rarr; <span>{tp[dasha.current_dasha.sukshuma] || dasha.current_dasha.sukshuma}</span> {t.sukshuma || 'Sukshuma'}</>
            )}
            {dasha.current_dasha.prana && (
              <> &rarr; <span>{tp[dasha.current_dasha.prana] || dasha.current_dasha.prana}</span> {t.prana || 'Prana'}</>
            )}
          </div>
        </CurrentDashaCard>

        {/* Dasha Balance at Birth */}
        <DashaBalanceCard>
          <DashaBalanceItem>
            <div className="label">{t.birthDashaLord}</div>
            <div className="value">{tp[dasha.dasha_balance.lord] || dasha.dasha_balance.lord}</div>
          </DashaBalanceItem>
          <DashaBalanceItem>
            <div className="label">{t.balanceAtBirth}</div>
            <div className="value">{formatDuration(dasha.dasha_balance.remaining_parts)}</div>
          </DashaBalanceItem>
          <DashaBalanceItem>
            <div className="label">{t.moonNakshatra}</div>
            <div className="value">{tn[dasha.moon_nakshatra] || dasha.moon_nakshatra}</div>
          </DashaBalanceItem>
        </DashaBalanceCard>

        {/* Mahadasha Timeline — click to drill down */}
        <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>{t.mahadashaPeriods}</h3>
        <MahaDashaList>
          {dasha.mahadashas.map((md, i) => (
            <MahaDashaRow key={i} isCurrent={md.is_current} onClick={() => openBukthi(md)} style={{ cursor: 'pointer' }}>
              <div className="lord">{tp[md.lord] || md.lord}</div>
              <div className="years">{formatDuration(md.duration_parts)}</div>
              <div className="dates">{md.start_date}</div>
              <div className="dates">{md.end_date}</div>
            </MahaDashaRow>
          ))}
        </MahaDashaList>
        <DashaClickHint>{t.dashaClickHint || 'Click a Mahadasha to see Bukthi periods'}</DashaClickHint>
      </Card>

      {/* Hierarchical Dasha Timeline (shared component) */}
      <Card style={{ marginTop: 20 }}>
        <CardTitle>{t.dashaTimeline || 'Dasha Timeline'}</CardTitle>
        <DashaTimeline periods={dasha.mahadashas} levels={3} highlightCurrent />
      </Card>

      {/* TODO(#123): Remove popup dasha code after 2 weeks in production
      {popup && (
        <Overlay onClick={() => setPopup(null)}>
          <Popup onClick={e => e.stopPropagation()}>
            <PopupHeader>
              <h3>{levelLabels[popup.level]}</h3>
              <CloseBtn onClick={() => setPopup(null)}>&times;</CloseBtn>
            </PopupHeader>
            <Breadcrumb>
              {popup.breadcrumb.map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ' > '}
                  <span>{tp[b.lord] || b.lord}</span>
                </React.Fragment>
              ))}
              {' > '}{levelLabels[popup.level]}
            </Breadcrumb>
            <SubDashaList>
              {popup.items.map((item, i) => (
                <SubDashaRow
                  key={i}
                  isCurrent={item.is_current}
                  onClick={() => {
                    if (popup.level === 'bukthi') openAnthara(item, popup.breadcrumb);
                    else if (popup.level === 'anthara') openSukshuma(item, popup.breadcrumb);
                    else if (popup.level === 'sukshuma') openPrana(item, popup.breadcrumb);
                  }}
                  style={{ cursor: popup.level !== 'prana' ? 'pointer' : 'default' }}
                >
                  <div className="lord">{tp[item.lord] || item.lord}</div>
                  <div className="dates">{item.start_date}</div>
                  <div className="dates">{item.end_date}</div>
                  {item.is_current && <span style={{ color: '#B8860B', fontWeight: 600, fontSize: '0.75rem' }}>ACTIVE</span>}
                </SubDashaRow>
              ))}
            </SubDashaList>
            {popup.level !== 'prana' && (
              <DashaClickHint>
                {popup.level === 'bukthi' && (t.clickForAnthara || 'Click to see Anthara periods')}
                {popup.level === 'anthara' && (t.clickForSukshuma || 'Click to see Sukshuma periods')}
                {popup.level === 'sukshuma' && (t.clickForPrana || 'Click to see Prana periods')}
              </DashaClickHint>
            )}
          </Popup>
        </Overlay>
      )}
      */}
    </DashaSection>
  );
};

const YogasTab = ({ yogas, loc }) => {
  const t = loc.freeKundli;
  const tp = loc.planets;
  const yn = loc.yogaNames || {};
  const ys = loc.yogaSanskrit || {};
  const yd = loc.yogaDescriptions || {};
  const ystr = loc.yogaStrength || {};
  return (
    <Card>
      <CardTitle>{t.yogasFound} ({yogas.length})</CardTitle>
      {yogas.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888' }}>{t.noYogas}</p>
      ) : (
        yogas.map((y, i) => (
          <YogaCard key={i} category={y.category}>
            <YogaHeader>
              <div>
                <span className="name">{yn[y.name] || y.name}</span>
                {y.sanskrit && <span className="sanskrit">{ys[y.name] || y.sanskrit}</span>}
              </div>
              <YogaBadge strength={y.strength}>{ystr[y.strength] || y.strength}</YogaBadge>
            </YogaHeader>
            <YogaDesc>{yd[y.name] || y.description}</YogaDesc>
            <PlanetTags>
              {(y.planets || []).map((p, j) => (
                <PlanetTag key={j}>{tp[p] || p}</PlanetTag>
              ))}
            </PlanetTags>
          </YogaCard>
        ))
      )}
    </Card>
  );
};

const DoshasTab = ({ doshas, loc }) => {
  const t = loc.freeKundli;
  const dn = loc.doshaNames || {};
  const dd = loc.doshaDescriptions || {};
  const ds = loc.doshaSeverity || {};
  const dr = loc.doshaRemedies || {};
  return (
    <Card>
      <CardTitle>{t.doshaAnalysis}</CardTitle>
      {doshas.map((d, i) => (
        <DoshaCard key={i} present={d.present} severity={d.severity}>
          <DoshaHeader>
            <span className="name">{dn[d.name] || d.name}</span>
            <DoshaStatus present={d.present}>
              {d.present ? (d.severity ? (ds[d.severity] || d.severity.toUpperCase()) : t.present) : t.notPresent}
            </DoshaStatus>
          </DoshaHeader>
          <YogaDesc>{dd[d.description] || d.description}</YogaDesc>
          {d.cancellations && d.cancellations.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: '0.85rem', color: '#2e7d32' }}>{t.cancellations}:</strong>
              <RemedyList>
                {d.cancellations.map((c, j) => <li key={j}>{dr[c] || c}</li>)}
              </RemedyList>
            </div>
          )}
          {d.present && d.remedies && d.remedies.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: '0.85rem', color: '#e65100' }}>{t.remedies}:</strong>
              <RemedyList>
                {d.remedies.map((r, j) => <li key={j}>{dr[r] || r}</li>)}
              </RemedyList>
            </div>
          )}
        </DoshaCard>
      ))}
    </Card>
  );
};

const PanchangaTab = ({ panchanga, loc }) => {
  const t = loc.freeKundli;
  const tn = loc.nakshatras;
  const tp = loc.planets;
  const tithiN = loc.tithiNames || {};
  const pakshaN = loc.pakshaNames || {};
  const varaN = loc.varaNames || {};
  const pyogaN = loc.panchangaYogaNames || {};
  const karanaN = loc.karanaNames || {};
  return (
    <Card>
      <CardTitle>{t.panchangaTitle}</CardTitle>
      <PanchangaGrid>
        <PanchangaItem>
          <div className="label">{t.tithi}</div>
          <div className="value">{tithiN[panchanga.tithi?.name] || panchanga.tithi?.name}</div>
          <div className="sub">{pakshaN[panchanga.tithi?.paksha] || panchanga.tithi?.paksha} {t.paksha}</div>
        </PanchangaItem>
        <PanchangaItem>
          <div className="label">{t.vara}</div>
          <div className="value">{varaN[panchanga.vara?.name] || panchanga.vara?.name}</div>
          <div className="sub">{t.lord}: {tp[panchanga.vara?.lord] || panchanga.vara?.lord}</div>
        </PanchangaItem>
        <PanchangaItem>
          <div className="label">{t.nakshatra}</div>
          <div className="value">{tn[panchanga.nakshatra?.name] || panchanga.nakshatra?.name}</div>
          <div className="sub">{t.colPada} {panchanga.nakshatra?.pada} &middot; {t.lord}: {tp[panchanga.nakshatra?.lord] || panchanga.nakshatra?.lord}</div>
        </PanchangaItem>
        <PanchangaItem>
          <div className="label">{t.yoga}</div>
          <div className="value">{pyogaN[panchanga.yoga?.name] || panchanga.yoga?.name}</div>
          <div className="sub">{panchanga.yoga?.meaning}</div>
        </PanchangaItem>
        <PanchangaItem>
          <div className="label">{t.karana}</div>
          <div className="value">{karanaN[panchanga.karana?.name] || panchanga.karana?.name}</div>
          <div className="sub">{panchanga.karana?.type}</div>
        </PanchangaItem>
      </PanchangaGrid>
    </Card>
  );
};

/* ─── Shadbala Tab ─── */
const ShadbalaTab = ({ shadbala, loc }) => {
  const tp = loc.planets || {};
  const t = loc.freeKundli;
  const signAbbrevs = loc.signAbbrevs || [];
  return (
    <Card>
      <CardTitle>{t.tabShadbala || 'Shadbala (Sixfold Strength)'}</CardTitle>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
              <th style={{ padding: '8px' }}>{t.colPlanet || 'Planet'}</th>
              <th style={{ padding: '8px' }}>Sthana</th>
              <th style={{ padding: '8px' }}>Dig</th>
              <th style={{ padding: '8px' }}>Kala</th>
              <th style={{ padding: '8px' }}>Cheshta</th>
              <th style={{ padding: '8px' }}>Naisargika</th>
              <th style={{ padding: '8px' }}>Drik</th>
              <th style={{ padding: '8px', fontWeight: 700 }}>Total</th>
              <th style={{ padding: '8px' }}>Ratio</th>
              <th style={{ padding: '8px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {(shadbala.planets || []).map((p, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee', background: p.is_strong ? '#e8f5e9' : '#fff8e1' }}>
                <td style={{ padding: '8px', fontWeight: 600 }}>{tp[p.planet] || p.planet}</td>
                <td style={{ padding: '8px' }}>{p.sthana_bala}</td>
                <td style={{ padding: '8px' }}>{p.dig_bala}</td>
                <td style={{ padding: '8px' }}>{p.kala_bala}</td>
                <td style={{ padding: '8px' }}>{p.cheshta_bala}</td>
                <td style={{ padding: '8px' }}>{p.naisargika_bala}</td>
                <td style={{ padding: '8px' }}>{p.drik_bala}</td>
                <td style={{ padding: '8px', fontWeight: 700 }}>{p.total_shadbala}</td>
                <td style={{ padding: '8px' }}>{p.shadbala_ratio}</td>
                <td style={{ padding: '8px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                    background: p.is_strong ? '#4caf50' : '#ff9800', color: '#fff'
                  }}>
                    {p.is_strong ? (loc.lang === 'ta' ? 'வலிமை' : 'Strong') : (loc.lang === 'ta' ? 'பலவீனம்' : 'Weak')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

/* ─── Ashtakavarga Tab ─── */
const AshtakavargaTab = ({ ashtakavarga, ascendantSign, chart, loc }) => {
  const tp = loc.planets || {};
  const t = loc.freeKundli;
  const isTa = loc.lang === 'ta';
  const interp = ashtakavarga.interpretation;
  const dir = ashtakavarga.direction;
  const analysis = ashtakavarga.analysis;
  const [selectedPlanet, setSelectedPlanet] = React.useState(null);

  const planetAshtaka = ashtakavarga.planet_ashtaka || [];

  // Build planet-name → sign_index map from birth chart
  const planetSignMap = React.useMemo(() => {
    const map = {};
    if (chart?.planets) {
      chart.planets.forEach(p => { map[p.planet] = p.sign_index; });
    }
    return map;
  }, [chart]);

  // Color for planet button based on total BAV score
  const getPlanetColor = (total) => {
    if (total >= 50) return { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' };
    if (total >= 40) return { bg: '#fff3e0', border: '#ff9800', text: '#e65100' };
    return { bg: '#ffebee', border: '#f44336', text: '#c62828' };
  };

  // SAV cell color callback
  const getSavCellColor = (score) => {
    if (score >= 30) return '#e8f5e9';
    if (score < 25) return '#ffebee';
    return '#fff3e0';
  };

  // Find selected planet data
  const selectedData = selectedPlanet === 'ASC'
    ? { planet: 'ASC', points: ashtakavarga.sarvashtaka || [], total: ashtakavarga.total_points }
    : selectedPlanet === 'SAV'
    ? { planet: 'SAV', points: ashtakavarga.sarvashtaka || [], total: ashtakavarga.total_points }
    : planetAshtaka.find(p => p.planet === selectedPlanet);

  // Determine which sign to highlight with grey overlay for the selected planet
  const highlightSign = selectedPlanet && selectedPlanet !== 'SAV' && selectedPlanet !== 'ASC'
    ? (planetSignMap[selectedPlanet] ?? -1)
    : -1;

  return (
    <Card>
      <CardTitle>{t.tabAshtakavarga || 'Ashtakavarga'}</CardTitle>

      {/* Planet Buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, justifyContent: 'center' }}>
        {planetAshtaka.map((pa) => {
          const colors = getPlanetColor(pa.total);
          const isActive = selectedPlanet === pa.planet;
          return (
            <button
              key={pa.planet}
              onClick={() => setSelectedPlanet(isActive ? null : pa.planet)}
              style={{
                padding: '10px 16px', borderRadius: 10, cursor: 'pointer',
                border: `2px solid ${isActive ? '#333' : colors.border}`,
                background: isActive ? '#333' : colors.bg,
                color: isActive ? '#fff' : colors.text,
                fontWeight: 700, fontSize: '0.9rem', minWidth: 90,
                textAlign: 'center', transition: 'all 0.2s',
              }}
            >
              <div>{tp[pa.planet] || pa.planet}</div>
              <div style={{ fontSize: '1.2rem', marginTop: 2 }}>{pa.total}</div>
            </button>
          );
        })}
        {/* ASC Button */}
        <button
          onClick={() => setSelectedPlanet(selectedPlanet === 'ASC' ? null : 'ASC')}
          style={{
            padding: '10px 16px', borderRadius: 10, cursor: 'pointer',
            border: `2px solid ${selectedPlanet === 'ASC' ? '#333' : '#B8860B'}`,
            background: selectedPlanet === 'ASC' ? '#333' : '#fdf6e3',
            color: selectedPlanet === 'ASC' ? '#fff' : '#B8860B',
            fontWeight: 700, fontSize: '0.9rem', minWidth: 90,
            textAlign: 'center', transition: 'all 0.2s',
          }}
        >
          <div>{isTa ? 'லக்னம்' : 'ASC'}</div>
          <div style={{ fontSize: '0.8rem', marginTop: 2 }}>{isTa ? 'சர்வாஷ்டக' : 'SAV View'}</div>
        </button>
        {/* SAV Button */}
        <button
          onClick={() => setSelectedPlanet(selectedPlanet === 'SAV' ? null : 'SAV')}
          style={{
            padding: '10px 16px', borderRadius: 10, cursor: 'pointer',
            border: `2px solid ${selectedPlanet === 'SAV' ? '#333' : '#1565c0'}`,
            background: selectedPlanet === 'SAV' ? '#333' : '#e3f2fd',
            color: selectedPlanet === 'SAV' ? '#fff' : '#1565c0',
            fontWeight: 700, fontSize: '0.9rem', minWidth: 90,
            textAlign: 'center', transition: 'all 0.2s',
          }}
        >
          <div>SAV</div>
          <div style={{ fontSize: '1.2rem', marginTop: 2 }}>{ashtakavarga.total_points}</div>
        </button>
      </div>

      {/* Chakra Popup/Modal */}
      {selectedData && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setSelectedPlanet(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: 20, maxWidth: 460, width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                {selectedPlanet === 'ASC' ? (isTa ? 'லக்னம் — சர்வாஷ்டகவர்கம்' : 'ASC — Sarvashtakavarga')
                  : selectedPlanet === 'SAV' ? (isTa ? 'சர்வாஷ்டகவர்கம்' : 'Sarvashtakavarga')
                  : `${tp[selectedData.planet] || selectedData.planet} ${isTa ? 'அஷ்டகவர்கம்' : 'Ashtakavarga'}`}
              </h3>
              <button
                onClick={() => setSelectedPlanet(null)}
                style={{ border: 'none', background: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#666' }}
              >
                ✕
              </button>
            </div>
            <AshtavargaChakra
              scores={selectedData.points}
              title={selectedPlanet === 'ASC' ? `ASC — SAV (${selectedData.total})` : selectedPlanet === 'SAV' ? `SAV (${selectedData.total})` : `${selectedData.planet} (${selectedData.total})`}
              ascendantSign={ascendantSign != null ? ascendantSign : -1}
              highlightSign={highlightSign}
              getCellColor={selectedPlanet === 'SAV' || selectedPlanet === 'ASC' ? getSavCellColor : undefined}
              loc={loc}
            />
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.8rem', color: '#888' }}>
              {isTa ? 'லக்னம் புள்ளிக்கோட்டால் காட்டப்பட்டுள்ளது' : 'ASC sign highlighted with dashed border'}
            </div>
          </div>
        </div>
      )}

      {/* Direction */}
      {dir && (
        <div style={{ marginTop: 16, padding: 12, background: '#f3e5f5', borderRadius: 8 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>{isTa ? 'சிறந்த திசை' : 'Auspicious Direction'}</h4>
          <div style={{ fontSize: '0.85rem' }}>
            {isTa ? 'சிறந்தது' : 'Best'}: <strong>{dir.best_direction}</strong> ({dir.best_sign}, SAV {dir.best_sav})
            &nbsp;&middot;&nbsp;
            {isTa ? 'மோசமானது' : 'Avoid'}: <strong>{dir.worst_direction}</strong> ({dir.worst_sign}, SAV {dir.worst_sav})
          </div>
        </div>
      )}

      {/* House Comparisons */}
      {interp && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: '0.95rem', marginBottom: 8 }}>{isTa ? 'வீடு ஒப்பீடுகள்' : 'House Comparisons'}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {interp.house_comparison.map((hc, i) => (
              <span key={i} style={{
                padding: '4px 10px', borderRadius: 16, fontSize: '0.78rem', fontWeight: 600,
                background: hc.met ? '#e8f5e9' : '#ffebee', color: hc.met ? '#2e7d32' : '#c62828',
              }}>
                {hc.rule}: {hc.met ? '✓' : '✗'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Trikona Groups */}
      {interp && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: '0.95rem', marginBottom: 8 }}>{isTa ? 'திரிகோண குழுக்கள்' : 'Trikona Groups'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            {interp.trikona_groups.map((tg, i) => (
              <div key={i} style={{ padding: 10, background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{tg.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{isTa ? 'வீடுகள்' : 'Houses'}: {tg.houses}</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1565c0' }}>{tg.total}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Financial + Sade Sati + Longevity */}
      {analysis && (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
          {analysis.financial && (
            <div style={{ padding: 12, background: '#e8f5e9', borderRadius: 8 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>{isTa ? 'நிதி பகுப்பாய்வு' : 'Financial Analysis'}</h4>
              <div style={{ fontSize: '0.82rem' }}>
                <div>{isTa ? 'செல்வ ஆற்றல்' : 'Wealth'}: <strong>{analysis.financial.wealth_potential}</strong> ({analysis.financial.wealth_verdict})</div>
                <div>{isTa ? 'தொழில் வலிமை' : 'Career'}: <strong>{analysis.financial.career_strength}</strong></div>
                <div>{isTa ? 'செலவு' : 'Expense'}: {analysis.financial.expense_risk} ({analysis.financial.expense_verdict})</div>
              </div>
            </div>
          )}
          {analysis.sade_sati && (
            <div style={{ padding: 12, background: analysis.sade_sati.severity === 'mild' ? '#e8f5e9' : analysis.sade_sati.severity === 'moderate' ? '#fff8e1' : '#ffebee', borderRadius: 8 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>{isTa ? 'சாடே சாதி' : 'Sade Sati'}</h4>
              <div style={{ fontSize: '0.82rem' }}>
                <div>SAV Total: <strong>{analysis.sade_sati.total_sav}</strong></div>
                <div>{isTa ? 'தீவிரம்' : 'Severity'}: <strong style={{ textTransform: 'uppercase' }}>{analysis.sade_sati.severity}</strong></div>
              </div>
            </div>
          )}
          {analysis.longevity && (
            <div style={{ padding: 12, background: '#e3f2fd', borderRadius: 8 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>{isTa ? 'ஆயுள் பகுப்பாய்வு' : 'Longevity'}</h4>
              <div style={{ fontSize: '0.82rem' }}>
                <div>{isTa ? 'கேந்திரம்' : 'Kendra'}: {analysis.longevity.kendra_total} | {isTa ? 'பணபரம்' : 'Panapara'}: {analysis.longevity.panapara_total} | {isTa ? 'அபோக்லிமா' : 'Apoklima'}: {analysis.longevity.apoklima_total}</div>
                <div>{isTa ? 'உயிர்ப்பு' : 'Vitality'}: <strong>{analysis.longevity.vitality_ratio}</strong> ({analysis.longevity.vitality_verdict})</div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/* ─── Divisional Charts Tab ─── */
const PLANET_ABBREVS = { Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke' };

const transformVargaToChartData = (vargaData) => {
  const ascSign = vargaData.ascendant?.sign ?? vargaData.ascendant_sign ?? 0;
  const ascName = vargaData.ascendant?.sign_name || '';
  return {
    ascendant: {
      planet: 'Ascendant',
      sign_index: ascSign,
      sign_name: ascName,
      abbrev: 'Asc',
    },
    planets: (vargaData.planets || vargaData.positions || []).map(p => ({
      planet: p.planet,
      sign_index: p.sign ?? p.sign_index ?? 0,
      sign_name: p.sign_name || '',
      abbrev: PLANET_ABBREVS[p.planet] || p.planet.substring(0, 2),
    })),
  };
};

const VargasTab = ({ vargas, loc }) => {
  const t = loc.freeKundli;
  const [selectedVarga, setSelectedVarga] = React.useState(null);

  const vargaLabels = {
    2: 'D2 - Hora', 3: 'D3 - Drekkana', 4: 'D4 - Chaturthamsa',
    7: 'D7 - Saptamsa', 9: 'D9 - Navamsa', 10: 'D10 - Dasamsa',
    12: 'D12 - Dwadasamsa', 16: 'D16 - Shodasamsa', 20: 'D20 - Vimsamsa',
    24: 'D24 - Chaturvimsamsa', 27: 'D27 - Saptavimsamsa', 30: 'D30 - Trimsamsa',
    40: 'D40 - Khavedamsa', 45: 'D45 - Akshavedamsa', 60: 'D60 - Shashtiamsa',
  };

  const vargaKeys = Object.keys(vargas || {}).map(Number).sort((a, b) => a - b);
  const activeVarga = selectedVarga || vargaKeys[0];
  const vargaData = vargas?.[activeVarga];

  return (
    <Card>
      <CardTitle>{t.tabVargas || 'Divisional Charts'}</CardTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {vargaKeys.map(k => (
          <button key={k} onClick={() => setSelectedVarga(k)} style={{
            padding: '4px 10px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.8rem',
            background: k === activeVarga ? '#B8860B' : '#fff',
            color: k === activeVarga ? '#fff' : '#333', cursor: 'pointer',
          }}>{vargaLabels[k] || `D${k}`}</button>
        ))}
      </div>
      {vargaData && (
        <ChartContainer
          chartData={transformVargaToChartData(vargaData)}
          title={vargaLabels[activeVarga] || `D${activeVarga}`}
          loc={loc}
        />
      )}
    </Card>
  );
};

/* ─── House Analysis Tab ─── */
const HouseAnalysisTab = ({ houseAnalysis, loc }) => {
  const tp = loc.planets || {};
  const ts = loc.signs || {};
  const t = loc.freeKundli;
  const signNames = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];

  return (
    <Card>
      <CardTitle>{t.tabHouses || 'House Analysis'}</CardTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {(houseAnalysis.houses || []).map((h, i) => {
          const sc = { bg: h.color || '#fff8e1', color: '#333' };
          return (
            <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {loc.lang === 'ta' ? `${h.number}-ஆம் வீடு` : `House ${h.number}`}
                </span>
                <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: sc.bg, color: sc.color }}>
                  {loc.lang === 'ta' ? (h.strength === 'strong' ? 'வலிமை' : h.strength === 'weak' ? 'பலவீனம்' : 'சாதாரணம்') : h.strength}
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: 6 }}>
                {ts[signNames[h.sign]] || signNames[h.sign]} &middot; {loc.lang === 'ta' ? 'அதிபதி' : 'Lord'}: {tp[h.lord] || h.lord}
              </div>
              {h.planets.length > 0 && (
                <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>
                  {loc.lang === 'ta' ? 'கிரகங்கள்' : 'Planets'}: {h.planets.map(p => tp[p] || p).join(', ')}
                </div>
              )}
              <div style={{ fontSize: '0.8rem', color: '#777', fontStyle: 'italic' }}>
                {h.signification}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4 }}>
                {loc.lang === 'ta' ? 'அதிபதி வீடு' : 'Lord in House'}: {h.lord_house}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

/* ─── Bhava Bala Tab ─── */
const BhavaBalaTab = ({ bhavaBala, loc }) => {
  const tp = loc.planets || {};
  const ts = loc.signs || {};
  const t = loc.freeKundli;
  const isTa = loc.lang === 'ta';
  const signNames = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];

  const strengthLabel = (s) => {
    if (isTa) return s === 'strong' ? 'வலிமை' : s === 'moderate' ? 'மிதம்' : 'பலவீனம்';
    return s === 'strong' ? 'Strong' : s === 'moderate' ? 'Moderate' : 'Weak';
  };

  const radar = bhavaBala.life_radar;
  const strongest = bhavaBala.strongest_house;
  const weakest = bhavaBala.weakest_house;

  const radarLabels = isTa
    ? { career: 'தொழில்', wealth: 'செல்வம்', relationships: 'உறவுகள்', health: 'ஆரோக்கியம்', wisdom: 'ஞானம்' }
    : { career: 'Career', wealth: 'Wealth', relationships: 'Relationships', health: 'Health', wisdom: 'Wisdom' };

  const radarFormulas = {
    career: 'H10 × 50% + H6 × 25% + H2 × 25%',
    wealth: 'H2 × 50% + H11 × 35% + H9 × 15%',
    relationships: 'H7 × 50% + H5 × 25% + H1 × 25%',
    health: 'H1 × 50% + H6 × 25% + H8 × 25%',
    wisdom: 'H5 × 40% + H9 × 40% + H4 × 20%',
  };

  const lifeAreas = isTa
    ? ['ஆளுமை & ஆரோக்கியம்', 'செல்வம் & குடும்பம்', 'தைரியம் & தகவல்', 'வீடு & கல்வி', 'குழந்தை & அறிவு', 'ஆரோக்கியம் & போட்டி', 'திருமணம் & கூட்டு', 'ஆயுள் & மாற்றம்', 'அதிர்ஷ்டம் & கல்வி', 'தொழில் & அந்தஸ்து', 'லாபம் & லட்சியம்', 'செலவு & விடுதலை']
    : null;

  return (
    <Card>
      <CardTitle>{isTa ? 'பாவ பலம் (வீட்டு வலிமை)' : 'Bhava Bala (House Strength)'}</CardTitle>

      {/* Strongest / Weakest summary */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        {strongest && (
          <div style={{ flex: 1, minWidth: 200, padding: 14, borderRadius: 10, background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
            <div style={{ fontSize: '0.78rem', color: '#2e7d32', fontWeight: 600, marginBottom: 4 }}>
              {isTa ? 'வலிமையான வீடு' : 'Strongest House'}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
              {isTa ? `${strongest.house}-ஆம் வீடு` : `House ${strongest.house}`}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#555' }}>
              {lifeAreas ? lifeAreas[strongest.house - 1] : strongest.life_area} &middot; {strongest.total_rupas} {isTa ? 'ரூபா' : 'rupas'}
            </div>
          </div>
        )}
        {weakest && (
          <div style={{ flex: 1, minWidth: 200, padding: 14, borderRadius: 10, background: '#ffebee', border: '1px solid #ef9a9a' }}>
            <div style={{ fontSize: '0.78rem', color: '#c62828', fontWeight: 600, marginBottom: 4 }}>
              {isTa ? 'பலவீனமான வீடு' : 'Weakest House'}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
              {isTa ? `${weakest.house}-ஆம் வீடு` : `House ${weakest.house}`}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#555' }}>
              {lifeAreas ? lifeAreas[weakest.house - 1] : weakest.life_area} &middot; {weakest.total_rupas} {isTa ? 'ரூபா' : 'rupas'}
            </div>
          </div>
        )}
      </div>

      {/* Life Radar */}
      {radar && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{isTa ? 'வாழ்க்கை ரேடார்' : 'Life Radar'}</span>
            <span title={isTa ? 'ஒவ்வொரு பரிமாணமும் தொடர்புடைய வீடுகளின் பாவ பல மதிப்புகளின் எடையிடப்பட்ட சராசரி' : 'Each dimension is a weighted average of related house Bhava Bala values (in rupas)'}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: '#e0e0e0', color: '#555', fontSize: '0.7rem', fontWeight: 700, cursor: 'help' }}>?</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 10 }}>
            {Object.entries(radarLabels).map(([key, label]) => {
              const val = radar[key] || 0;
              const pct = Math.min(val / 10 * 100, 100);
              return (
                <div key={key} style={{ textAlign: 'center', padding: 10, background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0' }}
                  title={radarFormulas[key]}>
                  <div style={{ fontSize: '0.8rem', color: '#777', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: val > 6 ? '#2e7d32' : val > 3 ? '#f57f17' : '#c62828' }}>{val}</div>
                  <div style={{ height: 4, borderRadius: 2, background: '#e0e0e0', marginTop: 4 }}>
                    <div style={{ height: 4, borderRadius: 2, width: `${pct}%`, background: val > 6 ? '#4caf50' : val > 3 ? '#ff9800' : '#f44336' }} />
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#aaa', marginTop: 4 }}>{radarFormulas[key]}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* House Strength Table — 9 components */}
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: '0.95rem' }}>
        {isTa ? '12 வீடுகளின் பலம் (விருபா)' : 'House Strength (virupas)'}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
              <th style={{ padding: '8px' }}>{isTa ? 'வீடு' : 'House'}</th>
              <th style={{ padding: '8px' }}>{isTa ? 'ராசி' : 'Sign'}</th>
              <th style={{ padding: '8px' }}>{isTa ? 'அதிபதி' : 'Lord'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Bhavadhipati">{isTa ? 'அதிபதி' : 'Lord'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Dig Bala">{isTa ? 'திக்' : 'Dig'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Drishti Bala">{isTa ? 'திருஷ்டி' : 'Drishti'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Adhipati Bala">{isTa ? 'இடம்' : 'Place'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Subha Drishti">+{isTa ? 'பார்வை' : 'Asp'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Subha Sandhana">+{isTa ? 'இணை' : 'Occ'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Papa Drishti">&minus;{isTa ? 'பார்வை' : 'Asp'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="Papa Sandhana">&minus;{isTa ? 'இணை' : 'Occ'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }} title="SAV Bala">SAV</th>
              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 700 }}>{isTa ? 'மொத்தம்' : 'Total'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>{isTa ? 'ரூபா' : 'Rupas'}</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>#</th>
              <th style={{ padding: '8px' }}>{isTa ? 'நிலை' : 'Status'}</th>
            </tr>
          </thead>
          <tbody>
            {(bhavaBala.houses || []).map((h, i) => {
              const sc = { bg: h.color || '#9e9e9e', color: '#fff' };
              return (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px', fontWeight: 600 }}>{h.house}</td>
                  <td style={{ padding: '8px' }}>{ts[signNames[h.sign]] || h.sign_name}</td>
                  <td style={{ padding: '8px' }}>{tp[h.lord] || h.lord}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{h.bhavadhipati_bala}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{h.dig_bala}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: h.drishti_bala < 0 ? '#c62828' : '#333' }}>{h.drishti_bala}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{h.adhipati_bala}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#2e7d32' }}>{h.subha_drishti}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#2e7d32' }}>{h.subha_sandhana}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#c62828' }}>{h.papa_drishti}</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#c62828' }}>{h.papa_sandhana}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{h.sav_bala}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700 }}>{h.total_virupas}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{h.total_rupas}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{h.rank}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, background: sc.bg, color: sc.color }}>
                      {strengthLabel(h.strength)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Life area cards */}
      <div style={{ fontWeight: 600, marginTop: 20, marginBottom: 10, fontSize: '0.95rem' }}>
        {isTa ? 'வீடு வாரியான வாழ்க்கை பகுதிகள்' : 'Life Areas by House'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {(bhavaBala.houses || []).sort((a, b) => a.rank - b.rank).map((h) => {
          const sc = { bg: h.color || '#9e9e9e', color: '#fff' };
          return (
            <div key={h.house} style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 14, background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 700 }}>
                  #{h.rank} &middot; {isTa ? `${h.house}-ஆம் வீடு` : `House ${h.house}`}
                </span>
                <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, background: sc.bg, color: sc.color }}>
                  {h.total_rupas} {isTa ? 'ரூபா' : 'rupas'}
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#555' }}>
                {ts[signNames[h.sign]] || h.sign_name} &middot; {tp[h.lord] || h.lord}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#777', marginTop: 4 }}>
                {lifeAreas ? lifeAreas[h.house - 1] : h.life_area}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

/* ─── Vimshopaka Bala Tab ─── */
const VimshopkaTab = ({ vimshopaka, loc }) => {
  const tp = loc.planets || {};
  const ts = loc.signs || {};
  const isTa = loc.lang === 'ta';
  const signNames = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];

  const qualityColor = (q) => {
    switch (q) {
      case 'Excellent': return { bg: '#1b5e20', color: '#fff' };
      case 'Very Good': return { bg: '#4caf50', color: '#fff' };
      case 'Good': return { bg: '#8bc34a', color: '#fff' };
      case 'Average': return { bg: '#ff9800', color: '#fff' };
      case 'Weak': return { bg: '#f44336', color: '#fff' };
      default: return { bg: '#9e9e9e', color: '#fff' };
    }
  };

  const dignityColor = (d) => {
    if (d === 'Exalted') return '#1b5e20';
    if (d === 'Moolatrikona' || d === 'Own Sign') return '#2e7d32';
    if (d === 'Great Friend' || d === 'Friend') return '#558b2f';
    if (d === 'Neutral') return '#777';
    if (d === 'Enemy' || d === 'Great Enemy') return '#c62828';
    if (d === 'Debilitated') return '#b71c1c';
    return '#777';
  };

  const [expanded, setExpanded] = React.useState(null);

  return (
    <Card>
      <CardTitle>{isTa ? 'விம்சோபக பலம் (20-புள்ளி கௌரவ மதிப்பெண்)' : 'Vimshopaka Bala (20-Point Dignity Score)'}</CardTitle>
      <div style={{ fontSize: '0.82rem', color: '#777', marginBottom: 16 }}>
        {isTa
          ? '16 வர்க்க ஜாதகங்களில் ஒவ்வொரு கிரகத்தின் கௌரவத்தை (நிலை தரம்) அளவிடுகிறது. ஷட்பலத்திலிருந்து வேறுபட்டது.'
          : 'Measures each planet\'s dignity (placement quality) across all 16 divisional charts. Distinct from Shadbala.'}
      </div>

      {/* Summary table */}
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
              <th style={{ padding: '8px' }}>{isTa ? 'கிரகம்' : 'Planet'}</th>
              <th style={{ padding: '8px', textAlign: 'right' }}>{isTa ? 'மதிப்பெண்' : 'Score'}</th>
              <th style={{ padding: '8px' }}>{isTa ? 'தரம்' : 'Quality'}</th>
              <th style={{ padding: '8px', textAlign: 'center' }}>{isTa ? 'விவரம்' : 'Details'}</th>
            </tr>
          </thead>
          <tbody>
            {(vimshopaka.planets || []).map((p, i) => {
              const qc = qualityColor(p.quality);
              const barPct = Math.min(p.score / 20 * 100, 100);
              return (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px', fontWeight: 600 }}>{tp[p.planet] || p.planet}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                      <div style={{ width: 80, height: 6, borderRadius: 3, background: '#e0e0e0' }}>
                        <div style={{ width: `${barPct}%`, height: 6, borderRadius: 3, background: qc.bg }} />
                      </div>
                      <span style={{ fontWeight: 700, minWidth: 40 }}>{p.score}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, background: qc.bg, color: qc.color }}>
                      {p.quality}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <button onClick={() => setExpanded(expanded === i ? null : i)}
                      style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer', color: '#555' }}>
                      {expanded === i ? (isTa ? 'மூடு' : 'Close') : (isTa ? 'காண்க' : 'View')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Expanded varga breakdown */}
      {expanded !== null && vimshopaka.planets[expanded] && (
        <div style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>
            {tp[vimshopaka.planets[expanded].planet] || vimshopaka.planets[expanded].planet} — {isTa ? '16 வர்க்க விவரம்' : '16 Varga Breakdown'}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ padding: '6px', textAlign: 'left' }}>{isTa ? 'வர்க்கம்' : 'Varga'}</th>
                  <th style={{ padding: '6px' }}>{isTa ? 'ராசி' : 'Sign'}</th>
                  <th style={{ padding: '6px' }}>{isTa ? 'கௌரவம்' : 'Dignity'}</th>
                  <th style={{ padding: '6px', textAlign: 'right' }}>{isTa ? 'எடை' : 'Weight'}</th>
                  <th style={{ padding: '6px', textAlign: 'right' }}>{isTa ? 'பங்களிப்பு' : 'Contrib.'}</th>
                </tr>
              </thead>
              <tbody>
                {vimshopaka.planets[expanded].varga_breakdown.map((v, j) => (
                  <tr key={j} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px', fontWeight: 600 }}>{v.varga} <span style={{ fontWeight: 400, color: '#999' }}>{v.varga_name}</span></td>
                    <td style={{ padding: '6px' }}>{ts[signNames[v.sign]] || v.sign_name}</td>
                    <td style={{ padding: '6px', color: dignityColor(v.dignity), fontWeight: 600 }}>{v.dignity}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{v.weight}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600 }}>{v.contribution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};

export default FreeKundli;
